import { branchMeta, raisedMeta, useMultiBranch } from "@/lib/branchInfo";
import { useEffect, useState } from "react";
// doc-design-sr: standard card-list design
import { PageFrame, DocCard, ItemsPanel, DocMenu, useDocFlow, LinkProjectDialog, timeOf } from "@/components/procurement/DocKit";
import { FileText, GitBranch, FolderInput, Ban, Search as SearchIcon, Link2 as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, Check, X, ChevronDown, ChevronRight } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { UomSelect } from "@/components/UomSelect";
import { WbsItemSelect } from "@/components/procurement/WbsItemSelect";
import { Link2 } from "lucide-react";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { ApprovalReviewButton } from "@/components/procurement/ApprovalReview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDateTime, partyLabel } from "@/lib/format";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

import { confirmAction } from "@/lib/dialogs";
const EMPTY_ITEM = { description: "", quantity: "", uom_id: "", wbs_item_id: "" };
// Describe WORK here (e.g. "MMS piling — Nos", "Brick masonry — m3"), not materials.


function useSearch() {
  const [q, setQ] = useState("");
  return [q, setQ, (arr, text) => { const t = q.trim().toLowerCase(); return t ? arr.filter((r) => text(r).toLowerCase().includes(t)) : arr; }];
}
function SearchBox({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative w-full max-w-sm">
      <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10 rounded-lg pl-9" data-testid={testId} />
    </div>
  );
}
const viewBtn = "h-10 rounded-lg px-6 font-semibold";


export default function ServiceRequisitions() {
  const multi = useMultiBranch();
  const pf = useProjectFilter();
  const { can } = usePermissions();
  const canCreate = can("SERVICE_REQUISITIONS", "create");   // create and submit are both "Create" on the server
  const canDelete = can("SERVICE_REQUISITIONS", "delete");
  const canDecide = can("SERVICE_REQUISITIONS", "approve");
  const { companies, singleCompany } = useCompany() || {};
  const [srs, setSrs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [materialsByWbsId, setMaterialsByWbsId] = useState({});
  const [q, setQ, filt] = useSearch();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, projRes, woRes, venRes, uomRes] = await Promise.all([
        api.get("/procurement/service-requisitions"),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/procurement/work-orders").catch(() => ({ data: [] })),
        api.get("/procurement/vendors").catch(() => ({ data: [] })),
        api.get("/masters/uom").catch(() => ({ data: [] })),
      ]);
      setVendors(venRes.data || []);
      setUoms(uomRes.data || []);
      setSrs([...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setProjects(projRes.data);
      setWorkOrders(woRes.data);
      const projectIds = [...new Set(data.map((r) => r.project_id).filter(Boolean))];
      const lookups = await Promise.all(projectIds.map((pid) =>
        api.get(`/projects/${pid}/wbs`).then(({ data: wbs }) => wbs.map((w) => [w.id, `${w.code} — ${w.name}`])).catch(() => [])
      ));
      setMaterialsByWbsId(Object.fromEntries(lookups.flat()));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  const { openFlow, flowNode } = useDocFlow(load);

  const projectOf = (id) => projects.find((p) => p.id === id);
  const woNumber = (id) => workOrders.find((w) => w.id === id)?.number || "a work order";
  const vendorOf = (sr) => (sr.performed_by === "COMPANY_LABOUR" ? "Company labour" : sr.preferred_vendor_id ? partyLabel(vendors.find((v) => v.id === sr.preferred_vendor_id) || { name: "preferred vendor" }) : "No preferred vendor");
  const uomCode = (id) => uoms.find((u) => u.id === id)?.code || "—";

  const act = async (sr, action, message) => {
    setBusyId(sr.id);
    try {
      let done = message;
      if (action === "delete") await api.delete(`/procurement/service-requisitions/${sr.id}`);
      else {
        const { data: res } = await api.post(`/procurement/service-requisitions/${sr.id}/${action}`);
        // approving may have made the work order (or filled the planned scope) already: say so instead of "you can make one"
        if (action === "approve" && res?.status === "CONVERTED") done = res.work_order_id ? `${sr.number} approved — a draft Work Order was created from it. Add its rates under Work Orders` : `${sr.number} approved — the quantities were added to the activity's planned scope`;
      }
      toast.success(done);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (sr) => {
    if (!await confirmAction(`Delete service requisition ${sr.number}? This cannot be undone.`)) return;
    act(sr, "delete", `${sr.number} deleted`);
  };

  const rows = filt(pf.apply(srs, (r) => r.project_id), (s) => `${s.number} ${s.scope_of_work || ""} ${projectOf(s.project_id)?.code || ""}`);

  return (
    <PageFrame testId="service-requisitions-page" crumbs={[{ label: "Procurement" }, { label: "Service Requisitions" }]} title="Service Requisitions"
      description="Scope of work for a sub-contract, ahead of raising a Work Order."
      actions={<><ProjectFilter filter={pf} />{canCreate && <CreateSRDialog companies={companies} projects={projects} onCreated={load} />}</>}>
      <SearchBox value={q} onChange={setQ} placeholder="Search number, scope or project…" testId="sr-search" />
      {!loading && rows.length === 0 ? (
        <EmptyState title={srs.length ? "No service requisitions match" : "No service requisitions yet"} testId="service-requisitions-empty-state" />
      ) : (
        <div className="space-y-3">
          {rows.map((sr, idx) => {
            const open = expanded[sr.id] ?? (idx === 0 && !q);
            const items = sr.items || [];
            const p = projectOf(sr.project_id);
            return (
              <DocCard key={sr.id} testId={`sr-row-${sr.number}`} toggleId={`sr-toggle-${sr.number}`} open={open} onToggle={() => setExpanded((e) => ({ ...e, [sr.id]: !open }))}
                number={sr.number} date={sr.requisition_date} time={timeOf(sr.created_at)} meta={[...branchMeta(sr, multi), ...raisedMeta(sr, singleCompany), `${items.length} item${items.length === 1 ? "" : "s"}`, ...(p ? [p.code] : []), vendorOf(sr)]}
                line={sr.scope_of_work ? <span className="line-clamp-1 max-w-2xl">{sr.scope_of_work}</span> : null}
                note={sr.work_order_id ? `→ ${woNumber(sr.work_order_id)}` : null}
                right={<>
                  <StatusPill value={sr.status} />
                  {canCreate && ["DRAFT", "REJECTED"].includes(sr.status) && <Button size="sm" variant="outline" className="h-10 rounded-lg" disabled={busyId === sr.id} onClick={() => act(sr, "submit", `${sr.number} submitted for approval`)} data-testid={`sr-submit-button-${sr.number}`}><Send className="h-3.5 w-3.5" /> Submit</Button>}
                  {canDecide && sr.status === "SUBMITTED" && <>
                    <Button size="sm" className="h-10 rounded-lg" disabled={busyId === sr.id} onClick={() => act(sr, "approve", `${sr.number} approved — you can now make a Work Order from it`)} data-testid={`sr-approve-button-${sr.number}`}><Check className="h-3.5 w-3.5" /> Approve</Button>
                    <Button size="sm" variant="outline" className="h-10 rounded-lg" disabled={busyId === sr.id} onClick={() => act(sr, "reject", `${sr.number} rejected`)} data-testid={`sr-reject-button-${sr.number}`}><X className="h-3.5 w-3.5 text-rose-500" /> Reject</Button>
                  </>}
                  <ApprovalReviewButton docType="SR" docId={sr.id} onChanged={load} icon={FileText} className={viewBtn} />
                  <DocMenu testId={`sr-menu-${sr.number}`} items={[
                    canDelete && sr.status !== "CONVERTED" && { key: "del", label: "Delete", icon: Trash2, danger: true, onSelect: () => remove(sr), testId: `sr-delete-button-${sr.number}` },
                  ]} />
                </>}>
                <ItemsPanel testId={`sr-items-${sr.number}`}
                  columns={[{ label: "Work item", w: "3fr" }, { label: "Qty", w: "1fr", right: true }, { label: "Unit", w: "1fr" }, { label: "Linked activity", w: "2.5fr" }]}
                  rows={items.map((i) => [
                    i.description, Number(i.quantity), uomCode(i.uom_id),
                    i.wbs_item_id ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-400" data-testid={`sr-item-material-link-${i.id}`}><LinkIcon className="h-3 w-3" /> {materialsByWbsId[i.wbs_item_id] || "linked activity"}</span> : <span className="text-muted-foreground">—</span>,
                  ])} />
              </DocCard>
            );
          })}
        </div>
      )}
      {flowNode}
    </PageFrame>
  );
}

function CreateSRDialog({ companies, projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", project_id: "", requisition_date: "", scope_of_work: "", performed_by: "CONTRACTOR", preferred_vendor_id: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/procurement/vendors").then(({ data }) => setVendors(data || [])).catch(() => {});
  }, [open]);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Choosing an activity fills the line: description, quantity (what is still left of its scope) and unit.
  const fillFromActivity = async (idx, activity) => {
    const patchItem = (p) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...p } : it)));
    if (!activity) { patchItem({ description: "", _hint: "" }); return; }
    patchItem({ description: `${activity.code} — ${activity.name}` });
    try {
      const { data: bal } = await api.get("/procurement/sr-activity-balance", { params: { project_id: form.project_id, wbs_item_id: activity.id } });
      const key = (u) => String(u || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      let uomId = "";
      try {
        const { data: uoms } = await api.get("/masters/uom");
        uomId = (uoms || []).find((u) => key(u.code) === key(bal.uom))?.id || "";
      } catch { /* unit stays blank */ }
      const remaining = Number(bal.remaining);
      const unit = bal.uom || "";
      if (remaining > 0) {
        patchItem({ quantity: String(remaining), ...(uomId ? { uom_id: uomId } : {}),
          _hint: `Scope ${Number(bal.scope)} ${unit} · already requested ${Number(bal.requisitioned)} · remaining ${remaining}` });
      } else {
        patchItem({ ...(uomId ? { uom_id: uomId } : {}),
          _hint: Number(bal.scope) > 0 ? `Whole scope (${Number(bal.scope)} ${unit}) is already requested. Enter a quantity only if you need more.` : "This activity has no scope quantity yet. Set it in Plan setup, or type a quantity." });
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/service-requisitions", {
        ...form, project_id: form.project_id || null, preferred_vendor_id: form.performed_by === "CONTRACTOR" ? (form.preferred_vendor_id || null) : null,
        items: items.map(({ _hint, ...it }) => ({ ...it, uom_id: it.uom_id || null, wbs_item_id: it.wbs_item_id || null })),
      });
      toast.success("Service requisition created");
      setForm({ company_id: "", project_id: "", requisition_date: "", scope_of_work: "", performed_by: "CONTRACTOR", preferred_vendor_id: "" });
      setItems([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-sr-button"><Plus className="h-4 w-4" /> New Service Requisition</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Service Requisition</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-sr-form">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="sr-company-select" />
            <div><Label>Date</Label><Input required type="date" data-testid="sr-date-input" value={form.requisition_date} onChange={set("requisition_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Project</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="sr-project-select" className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Scope of work</Label><Textarea data-testid="sr-scope-input" value={form.scope_of_work} onChange={set("scope_of_work")} className="mt-1.5" /></div>
          <div>
            <Label>Done by</Label>
            <RadioGroup value={form.performed_by} onValueChange={(v) => setForm({ ...form, performed_by: v, preferred_vendor_id: "" })} className="mt-1.5 grid grid-cols-1 gap-2">
              <label className="flex items-start gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="CONTRACTOR" className="mt-0.5" data-testid="sr-done-by-contractor" />
                <span><span className="font-medium">Subcontractor</span><br /><span className="text-xs text-muted-foreground">Needs a vendor — picking a preferred vendor below auto-creates a draft Work Order once approved</span></span>
              </label>
              <label className="flex items-start gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="COMPANY_LABOUR" className="mt-0.5" data-testid="sr-done-by-company" />
                <span><span className="font-medium">Company Labour</span><br /><span className="text-xs text-muted-foreground">No vendor — approving adds this quantity straight into Construction DPR's planned scope for each linked activity</span></span>
              </label>
            </RadioGroup>
          </div>
          {form.performed_by === "CONTRACTOR" && (
            <div>
              <Label>Preferred vendor <span className="font-normal text-muted-foreground">(optional — picking one auto-creates a draft Work Order once this is approved)</span></Label>
              <Select value={form.preferred_vendor_id || "none"} onValueChange={(v) => setForm({ ...form, preferred_vendor_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="sr-preferred-vendor-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Decide later</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`sr-item-row-${idx}`}>
                  <div className="flex-1">
                    <WbsItemSelect
                      projectId={form.project_id} value={it.wbs_item_id}
                      onChange={(v) => updateItem(idx, "wbs_item_id", v)}
                      onPick={(activity) => fillFromActivity(idx, activity)}
                      testId={`sr-item-wbs-select-${idx}`}
                      allowNone={false}
                    />
                    {it._hint && <div className="mt-1 text-[11px] text-muted-foreground">{it._hint}</div>}
                  </div>
                  <Input required type="number" step="0.001" placeholder="Qty" className="w-20" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`sr-item-qty-input-${idx}`} />
                  <UomSelect byId value={it.uom_id} onChange={(v) => updateItem(idx, "uom_id", v)} className="w-28" testId={`sr-item-uom-select-${idx}`} />
                  {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-sr-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-sr-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
